/**
 * Lokdhara — Web Push registration helpers.
 *
 * `enableStaffPush({ api })`   — subscribes the logged-in admin/employee
 * `enableCustomerPush({ token, api })` — subscribes a portal customer
 *
 * The functions are idempotent: if the browser already has an active
 * PushSubscription for this origin, we re-POST it to the backend to keep
 * the server row fresh but never prompt the user again.
 *
 * SECURITY: notification taps NEVER route directly to the intended screen —
 * see `public/sw.js` and `NotifTapGate.jsx` for the enforced PIN re-auth.
 */

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window &&
        !!VAPID_PUBLIC_KEY
    );
}

async function ensureRegistration() {
    if (!("serviceWorker" in navigator)) return null;
    let reg = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!reg) {
        reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    }
    await navigator.serviceWorker.ready;
    return reg;
}

async function ensureSubscription(reg) {
    let sub = await reg.pushManager.getSubscription();
    if (sub) return sub;
    sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    return sub;
}

/** Request permission — non-blocking; returns "granted" | "denied" | "default". */
export async function requestPushPermission() {
    if (!pushSupported()) return "unsupported";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    try {
        return await Notification.requestPermission();
    } catch {
        return "default";
    }
}

/** Subscribe an authenticated staff user (admin / employee). */
export async function enableStaffPush({ api }) {
    if (!pushSupported()) return { ok: false, reason: "unsupported" };
    const perm = await requestPushPermission();
    if (perm !== "granted") return { ok: false, reason: perm };
    const reg = await ensureRegistration();
    if (!reg) return { ok: false, reason: "no-registration" };
    const sub = await ensureSubscription(reg);
    await api.post("/notifications/subscribe", {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent || "",
    });
    return { ok: true };
}

/** Subscribe a portal customer. Uses the raw axios `api` client + portal token. */
export async function enableCustomerPush({ api, token }) {
    if (!pushSupported() || !token) return { ok: false, reason: "unsupported" };
    const perm = await requestPushPermission();
    if (perm !== "granted") return { ok: false, reason: perm };
    const reg = await ensureRegistration();
    if (!reg) return { ok: false, reason: "no-registration" };
    const sub = await ensureSubscription(reg);
    await api.post(
        `/portal/notifications/subscribe`,
        {
            subscription: sub.toJSON(),
            user_agent: navigator.userAgent || "",
        },
        { params: { t: token } },
    );
    return { ok: true };
}

export async function disableStaffPush({ api }) {
    const reg = await ensureRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
        try {
            await api.delete("/notifications/subscribe", {
                data: { endpoint: sub.endpoint },
            });
        } catch { /* ignore */ }
        await sub.unsubscribe();
    }
}

/**
 * Attach a `notif-tap` listener bridge so the SW can hand the deep-link off
 * to the running app. Callers get `(deep, event)`.
 */
export function onNotifTap(handler) {
    if (!("serviceWorker" in navigator)) return () => {};
    const listener = (event) => {
        const d = event.data || {};
        if (d.type === "notif-tap") handler(d.deep || "/", d.event || "info");
    };
    navigator.serviceWorker.addEventListener("message", listener);
    return () => navigator.serviceWorker.removeEventListener("message", listener);
}
